/* eslint-disable import/no-extraneous-dependencies */
import { useMemo } from "react";
import type { Node, Edge } from "reactflow";
import type { ChartNode, ChartEdge } from "../../../../types/api-types";
import { calculateLayout, getCentralNodeId } from "../link-chart-helpers";
import getHandleForPosition from "../chart-edge-utils";
import {
  EDGE_STYLE,
  EDGE_LABEL_STYLE,
  EDGE_LABEL_BG_STYLE,
  EDGE_LABEL_CONFIG,
} from "../chart-constants";

/**
 * Hook to transform filtered chart data into React Flow nodes and edges
 */
const useChartTransforms = (
  filteredNodes: ChartNode[],
  filteredEdges: ChartEdge[],
) => {
  const positions = useMemo(
    () => calculateLayout(filteredNodes, filteredEdges),
    [filteredNodes, filteredEdges],
  );

  const centralNodeId = useMemo(
    () => getCentralNodeId(filteredNodes, filteredEdges),
    [filteredNodes, filteredEdges],
  );

  const initialNodes: Node[] = useMemo(
    () =>
      filteredNodes.map((node) => {
        const pos = positions.get(node.id) || { x: 0, y: 0 };
        return {
          id: node.id,
          type: "custom",
          position: pos,
          data: {
            label: node.label || node.entityTypeDescription,
            entityTypeDescription: node.entityTypeDescription,
            details: node.details,
          },
        };
      }),
    [filteredNodes, positions],
  );

  const initialEdges: Edge[] = useMemo(
    () =>
      filteredEdges.map((edge) => {
        const sourceNode = filteredNodes.find((n) => n.id === edge.node1Id);
        const targetNode = filteredNodes.find((n) => n.id === edge.node2Id);

        const sourcePos = sourceNode
          ? positions.get(sourceNode.id) || { x: 0, y: 0 }
          : { x: 0, y: 0 };
        const targetPos = targetNode
          ? positions.get(targetNode.id) || { x: 0, y: 0 }
          : { x: 0, y: 0 };

        const handles = getHandleForPosition(sourcePos, targetPos);

        // Use straight edges for connections to/from the central node
        const isCentralEdge =
          edge.node1Id === centralNodeId || edge.node2Id === centralNodeId;
        const edgeType = isCentralEdge ? "straight" : "smoothstep";

        return {
          id: edge.id,
          source: edge.node1Id,
          target: edge.node2Id,
          sourceHandle: handles.source,
          targetHandle: handles.target,
          label: edge.linkSubTypeDescription || edge.linkTypeDescription,
          type: edgeType,
          animated: false,
          style: EDGE_STYLE,
          labelStyle: EDGE_LABEL_STYLE,
          labelBgStyle: EDGE_LABEL_BG_STYLE,
          labelBgPadding: EDGE_LABEL_CONFIG.padding,
          labelBgBorderRadius: EDGE_LABEL_CONFIG.borderRadius,
        };
      }),
    [filteredEdges, filteredNodes, positions, centralNodeId],
  );

  return {
    initialNodes,
    initialEdges,
  };
};

export default useChartTransforms;
